import React, { useMemo, useState } from "react";
import { Navigate } from "react-router-dom";
import { useQuery } from "react-query";
import { cn } from "@/lib/utils";
import { inputClass, selectClass } from "@/lib/formStyles";
import DashboardWrapper from "../../components/Layout/DashboardWrapper";
import QueueHeader from "../../components/Queue/QueueHeader";
import AddToQueueModal from "../../components/Queue/AddToQueueModal";
import EditQueueEntryModal from "../../components/Queue/EditQueueEntryModal";
import RemoveQueueEntryModal from "../../components/Queue/RemoveQueueEntryModal";
import SecretaryQueueTable from "../../components/Queue/SecretaryQueueTable";
import QueueByDoctor from "../../components/Queue/QueueByDoctor";
import { QueueEmptyState, StatTile } from "../../components/Queue/QueueBits";
import { getUserData } from "../../utils/storageUtils";
import { isAdmin, isSecretary } from "../../constants/roles";
import { getTodayList } from "../../services/queue";
import { QueueEntryApi } from "../../services/queue/types";
import {
  computeQueueStats,
  patientDisplayName,
  WAIT_ALERT_MINUTES,
  WAIT_WARNING_MINUTES,
  waitLevel,
  WaitLevel,
} from "../../services/queue/stats";
import { colorStyle } from "../../services/visitReasons/types";
import useQueueStream from "../../hooks/useQueueStream";
import useVisitReasonsLookup from "../../hooks/queries/useVisitReasonsLookup";
import useDoctors from "../../hooks/queries/useDoctors";
import useTodayAppointments from "../../hooks/queries/useTodayAppointments";

type ViewMode = "arrival" | "doctor";

const todayLabel = () =>
  new Date().toLocaleDateString("fr-FR", { weekday: "long", day: "numeric", month: "long" });

/** Couleur de l'indicateur « Plus longue attente », alignée sur celle des lignes. */
const WAIT_LEVEL_TONE: Record<WaitLevel, "neutral" | "warning" | "alert"> = {
  normal: "neutral",
  warning: "warning",
  alert: "alert",
};

const filterChipClass = (active: boolean) =>
  cn(
    "inline-flex items-center gap-1.5 rounded-full border px-3 py-1 text-xs font-medium transition-colors duration-200",
    active ? "border-emerald-700 bg-emerald-700 text-white" : "border-slate-200 bg-white text-slate-600 hover:bg-slate-50"
  );

/**
 * Vue secrétaire (DEC-8 §5.4) : toute la liste du jour en FIFO, indicateurs,
 * filtres (nom, médecin, motif), ajout, correction et retrait d'une entrée.
 */
const QueueSecretaire: React.FC = () => {
  const userData = getUserData();
  const allowed = isSecretary(userData) || isAdmin(userData);

  const [entries, setEntries] = useState<QueueEntryApi[]>([]);
  const [search, setSearch] = useState("");
  const [doctorFilter, setDoctorFilter] = useState("");
  const [reasonFilter, setReasonFilter] = useState<number | null>(null);
  const [viewMode, setViewMode] = useState<ViewMode>("arrival");
  const [isAdding, setIsAdding] = useState(false);
  /** Ids et non copies : les fenêtres lisent l'entrée à jour et se ferment si elle quitte la file. */
  const [editedEntryId, setEditedEntryId] = useState<number | null>(null);
  const [removedEntryId, setRemovedEntryId] = useState<number | null>(null);

  const listQuery = useQuery(["queue-today-list"], () => getTodayList(), {
    enabled: allowed,
    refetchInterval: 60000, // filet de sécurité si le flux SSE tombe
    onSuccess: (result: any) => {
      if (result?.ok) setEntries((result.data ?? []) as QueueEntryApi[]);
    },
  });
  useQueueStream((queue) => setEntries(queue), allowed, "/queue/stream");

  const { data: visitReasons = [] } = useVisitReasonsLookup();
  const doctors = useDoctors(allowed);
  const appointmentsByPatient = useTodayAppointments();

  const stats = useMemo(() => computeQueueStats(entries), [entries]);
  const registeredPatientIds = useMemo(() => new Set(entries.map((entry) => entry.patientId)), [entries]);
  /** Rang dans la file complète (FIFO), affiché même quand un filtre est actif. */
  const rankById = useMemo(() => new Map(entries.map((entry, index) => [entry.id, index + 1])), [entries]);

  const editedEntry = entries.find((entry) => entry.id === editedEntryId) ?? null;
  const removedEntry = entries.find((entry) => entry.id === removedEntryId) ?? null;

  const countByReason = (visitReasonId: number) =>
    entries.filter((entry) => entry.visitReasonId === visitReasonId).length;
  /** Un motif qui n'a plus de patient n'a plus de pastille : le filtre retombe sur « Tous ». */
  const activeReasonFilter = reasonFilter != null && countByReason(reasonFilter) > 0 ? reasonFilter : null;

  const normalizedSearch = search.trim().toLowerCase();
  const visibleEntries = entries.filter(
    (entry) =>
      (activeReasonFilter == null || entry.visitReasonId === activeReasonFilter) &&
      (doctorFilter === "" || String(entry.assignedDoctorId) === doctorFilter) &&
      (normalizedSearch === "" || patientDisplayName(entry).toLowerCase().includes(normalizedSearch))
  );
  const doctorCount = new Set(
    entries.map((entry) => entry.assignedDoctorId).filter((doctorId) => doctorId != null)
  ).size;

  if (!allowed) {
    return <Navigate to="/" replace />;
  }

  return (
    <DashboardWrapper showSpinner={listQuery.isLoading && entries.length === 0}>
      <>
        <div className="space-y-8">
          <div className="animate-in fade-in slide-in-from-bottom-2 duration-500 fill-mode-both">
            <QueueHeader
              eyebrow={`Accueil · ${todayLabel()}`}
              title="Liste d'attente"
              subtitle={
                <>
                  <span className="font-semibold text-slate-700">
                    {stats.waitingCount} patient{stats.waitingCount > 1 ? "s" : ""}
                  </span>{" "}
                  présent{stats.waitingCount > 1 ? "s" : ""} · {doctorCount} médecin{doctorCount > 1 ? "s" : ""}
                </>
              }
              actions={
                <button
                  type="button"
                  onClick={() => setIsAdding(true)}
                  className="inline-flex items-center justify-center gap-2 rounded-xl bg-green-600 px-5 py-3 text-sm font-medium text-white shadow-lg shadow-green-600/25 transition-all duration-300 hover:bg-green-700 active:bg-green-800 focus:outline-none focus:ring-4 focus:ring-emerald-500/30"
                >
                  <i className="bx bx-plus text-lg" /> Ajouter un patient
                </button>
              }
            />
          </div>

          <section
            aria-label="Indicateurs"
            className="grid grid-cols-2 gap-4 lg:grid-cols-4 animate-in fade-in slide-in-from-bottom-2 duration-500 delay-100 fill-mode-both"
          >
            <StatTile
              label="En attente"
              value={stats.waitingCount}
              hint={`${stats.withAppointmentCount} avec RDV · ${stats.waitingCount - stats.withAppointmentCount} sans RDV`}
            />
            <StatTile
              label="Attente moyenne"
              value={stats.averageWaitMinutes != null ? `${stats.averageWaitMinutes} min` : "—"}
              hint="depuis l'arrivée"
            />
            <StatTile
              label="Plus longue attente"
              value={stats.longestWait?.waitingMinutes != null ? `${stats.longestWait.waitingMinutes} min` : "—"}
              hint={
                stats.longestWait
                  ? `${patientDisplayName(stats.longestWait)} · ${stats.longestWait.assignedDoctorName ?? ""}`
                  : undefined
              }
              tone={WAIT_LEVEL_TONE[waitLevel(stats.longestWait?.waitingMinutes ?? 0)]}
            />
            <StatTile
              label="Urgences"
              value={stats.urgentCount}
              hint="ordre d'arrivée conservé"
              tone={stats.urgentCount > 0 ? "alert" : "neutral"}
            />
          </section>

          {entries.length === 0 ? (
            <QueueEmptyState
              title="Liste d'attente vide"
              description="Ajoutez un patient avec le bouton ci-dessus ou depuis la page Patients."
            />
          ) : (
            <section className="space-y-4 animate-in fade-in slide-in-from-bottom-2 duration-500 delay-200 fill-mode-both">
              <div className="flex flex-wrap items-center gap-3">
                <label className="group relative block w-full max-w-md">
                  <span className="sr-only">Rechercher un patient dans la liste</span>
                  <span className="pointer-events-none absolute left-3.5 top-1/2 -translate-y-1/2 text-lg text-slate-400 group-focus-within:text-emerald-600">
                    <i className="bx bx-search" aria-hidden />
                  </span>
                  <input
                    type="text"
                    value={search}
                    onChange={(event) => setSearch(event.target.value)}
                    placeholder="Rechercher un patient…"
                    className={cn(inputClass, "mt-0 pl-11 pr-4")}
                  />
                </label>
                <label className="relative block">
                  <span className="sr-only">Filtrer par médecin</span>
                  <select
                    value={doctorFilter}
                    onChange={(event) => setDoctorFilter(event.target.value)}
                    className={cn(selectClass, "mt-0 w-auto pl-4 pr-10")}
                  >
                    <option value="">Tous les médecins</option>
                    {doctors.map((doctor) => (
                      <option key={doctor.id} value={doctor.id}>
                        {doctor.firstName} {doctor.lastName}
                      </option>
                    ))}
                  </select>
                  <i className="bx bx-chevron-down pointer-events-none absolute right-3 top-1/2 -translate-y-1/2 text-lg text-slate-500" />
                </label>
                <div role="group" aria-label="Affichage" className="ml-auto inline-flex gap-1 rounded-xl bg-slate-100 p-1">
                  {(
                    [
                      ["arrival", "Ordre d'arrivée"],
                      ["doctor", "Par médecin"],
                    ] as [ViewMode, string][]
                  ).map(([mode, label]) => (
                    <button
                      key={mode}
                      type="button"
                      aria-pressed={viewMode === mode}
                      onClick={() => setViewMode(mode)}
                      className={cn(
                        "h-9 rounded-lg px-3.5 text-[13px] font-medium transition-colors",
                        viewMode === mode ? "bg-white text-slate-900 shadow-sm" : "text-slate-500 hover:text-slate-700"
                      )}
                    >
                      {label}
                    </button>
                  ))}
                </div>
              </div>

              <div role="group" aria-label="Filtrer par motif" className="flex flex-wrap gap-2">
                <button
                  type="button"
                  aria-pressed={activeReasonFilter == null}
                  onClick={() => setReasonFilter(null)}
                  className={filterChipClass(activeReasonFilter == null)}
                >
                  Tous · {entries.length}
                </button>
                {visitReasons
                  .filter((reason) => countByReason(reason.id) > 0)
                  .map((reason) => (
                    <button
                      key={reason.id}
                      type="button"
                      aria-pressed={activeReasonFilter === reason.id}
                      onClick={() => setReasonFilter(reason.id)}
                      className={filterChipClass(activeReasonFilter === reason.id)}
                    >
                      <span className={cn("h-1.5 w-1.5 rounded-full", colorStyle(reason.color).dot)} aria-hidden />
                      {reason.label} · {countByReason(reason.id)}
                    </button>
                  ))}
              </div>

              {viewMode === "arrival" ? (
                <SecretaryQueueTable
                  entries={visibleEntries}
                  rankById={rankById}
                  onEdit={(entry) => setEditedEntryId(entry.id)}
                  onRemove={(entry) => setRemovedEntryId(entry.id)}
                />
              ) : (
                <QueueByDoctor
                  entries={visibleEntries}
                  rankById={rankById}
                  onEdit={(entry) => setEditedEntryId(entry.id)}
                  onRemove={(entry) => setRemovedEntryId(entry.id)}
                />
              )}

              <p className="text-xs text-slate-500">
                Ordre d'arrivée : premier arrivé, premier servi. Le motif ne change pas l'ordre. Attente : gris
                sous {WAIT_WARNING_MINUTES} min, ambre jusqu'à {WAIT_ALERT_MINUTES} min, rose au-delà.
              </p>
            </section>
          )}
        </div>

        <AddToQueueModal
          key={isAdding ? "open" : "closed"}
          isOpen={isAdding}
          appointmentsByPatient={appointmentsByPatient}
          registeredPatientIds={registeredPatientIds}
          onClose={() => setIsAdding(false)}
        />
        {editedEntry && (
          <EditQueueEntryModal entry={editedEntry} doctors={doctors} onClose={() => setEditedEntryId(null)} />
        )}
        {removedEntry && <RemoveQueueEntryModal entry={removedEntry} onClose={() => setRemovedEntryId(null)} />}
      </>
    </DashboardWrapper>
  );
};

export default QueueSecretaire;
